import { gql, useApolloClient } from "@apollo/client";
import { Field, FinalFormCheckbox } from "@comet/admin";
import { FormControlLabel } from "@mui/material";
import { CheckboxSection, InputField } from "@src/components/fields";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import Form from "@src/components/Form";
import CountrySelect from "@src/components/select/CountrySelect";
import {
    type GQLUpdateUserAdminMutation,
    type GQLUpdateUserAdminMutationVariables,
    type GQLUpdateUserInput,
    type GQLUserAdminQuery,
} from "@src/graphql.generated";
import { type FunctionComponent, type PropsWithChildren } from "react";
import { useIntl } from "react-intl";

// eslint-disable-next-line @comet/no-private-sibling-import
import { userFragment } from "./fragments.gql";

export const updateUserMutation = gql`
    mutation UpdateUserAdmin($id: ID!, $input: UpdateUserInput!) {
        updateUserAdmin(id: $id, input: $input) {
            id
            ...UserFragment
        }
    }
    ${userFragment}
`;

const EditForm: FunctionComponent<PropsWithChildren<GQLUserAdminQuery>> = ({ userAdmin }) => {
    const intl = useIntl();
    const client = useApolloClient();
    const { id, ...initialValues } = userAdmin;

    const submit = ({
        firstname,
        lastname,
        email,
        address,
        zipCode,
        city,
        phone,
        country,
        agb,
        newsletter,
        aboReminder,
        ecg,
    }: GQLUpdateUserInput) => {
        client.mutate<GQLUpdateUserAdminMutation, GQLUpdateUserAdminMutationVariables>({
            mutation: updateUserMutation,
            variables: {
                id,
                input: {
                    firstname,
                    lastname,
                    email,
                    address,
                    zipCode,
                    city,
                    phone,
                    country,
                    agb,
                    newsletter,
                    aboReminder,
                    ecg,
                },
            },
        });
    };

    return (
        <Form
            mode="edit"
            onSubmit={submit}
            initialValues={initialValues}
            title={intl.formatMessage({ id: "reviermanager.users.form.edit.title", defaultMessage: "Benutzerdaten" })}
        >
            <InputField name="firstname" label={intl.formatMessage({ id: "reviermanager.user.firstname", defaultMessage: "Vorname" })} />
            <InputField name="lastname" label={intl.formatMessage({ id: "reviermanager.user.lastname", defaultMessage: "Nachname" })} />
            <InputField name="email" label={intl.formatMessage({ id: "reviermanager.user.email", defaultMessage: "E-Mail" })} />
            <InputField name="phone" label={intl.formatMessage({ id: "reviermanager.user.phone", defaultMessage: "Telefon" })} />
            <InputField name="address" label={intl.formatMessage({ id: "reviermanager.user.address", defaultMessage: "Straße & Nr." })} />
            <InputField name="zipCode" label={intl.formatMessage({ id: "reviermanager.user.zipCode", defaultMessage: "PLZ" })} />
            <InputField name="city" label={intl.formatMessage({ id: "reviermanager.user.city", defaultMessage: "Ort" })} />
            <StaticSelectField
                name="country"
                label={intl.formatMessage({ id: "reviermanager.user.country", defaultMessage: "Land" })}
                component={CountrySelect}
                labelVariant="long"
            />
            {/* TODO implement business logic */}
            <CheckboxSection label={intl.formatMessage({ id: "reviermanager.user.form.dataPolicy", defaultMessage: "Newsletter und Datenschutz" })}>
                <Field name="agb" type="checkbox">
                    {(props) => (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "reviermanager.user.form.agb.label", defaultMessage: "AGB" })}
                            control={<FinalFormCheckbox {...props} />}
                        />
                    )}
                </Field>
                <Field name="newsletter" type="checkbox">
                    {(props) => (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "reviermanager.user.form.newsletter.label", defaultMessage: "Newsletter" })}
                            control={<FinalFormCheckbox {...props} />}
                        />
                    )}
                </Field>
                <Field name="aboReminder" type="checkbox">
                    {(props) => (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "reviermanager.user.form.aboReminder.label", defaultMessage: "Abo-Reminder" })}
                            control={<FinalFormCheckbox {...props} />}
                        />
                    )}
                </Field>
                <Field name="ecg" type="checkbox">
                    {(props) => (
                        <FormControlLabel
                            label={intl.formatMessage({ id: "reviermanager.user.form.ecg.label", defaultMessage: "ECG-Liste" })}
                            control={<FinalFormCheckbox {...props} />}
                        />
                    )}
                </Field>
            </CheckboxSection>
        </Form>
    );
};

export default EditForm;
